<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from '@/plugins/i18n'
import { useSearchStore } from '@/components/Search/store/index.ts'
import { STANDARDS_SEARCH_TAB, MIN_QUERY_LENGTH } from '@/components/Search/constants.ts'
import StandardsSuggestList from './StandardsSuggestList.ce.vue'
import type { SuggestRecord } from '@/components/Search/types.ts'
import AllFiltersModal from '@/components/Search/SearchBar/AllFiltersModal/index.ce.vue';
import { useSearchActions } from '@/components/Search/SearchResults/Composables/useSearchActions.ts'
import { STANDARDS_FILTER_KEYS } from '@/components/Search/constants.ts'
// @ts-ignore
import vClickOutside from 'click-outside-vue3'

//directives
const vClickOutsideAction = vClickOutside.directive

//props
const props = defineProps({
    forMobile: { type: Boolean, default: false }
})


const searchStore = useSearchStore()

// translations
const i18nInstance: any = useI18n()
const { t } = i18nInstance

const suggestions = ref<SuggestRecord[]>([])
const showSuggestions = ref(false)
const isLoadingSuggestions = ref(false)
const highlightedIndex = ref(-1)
const displayAllFiltersMobile = ref(false);

let debounceTimeout: number | undefined

const clearSuggestions = () => {
  suggestions.value = []
  showSuggestions.value = false
  highlightedIndex.value = -1
}

const clearSearch = () => {
  searchStore['standards.documentNumber'] = ''
  clearSuggestions()
}

const fetchSuggestions = async (query: string) => {
  if (!query || query.length < MIN_QUERY_LENGTH) {
    clearSuggestions()
    return
  }

  isLoadingSuggestions.value = true
  showSuggestions.value = true
  suggestions.value = []
  highlightedIndex.value = -1

  try {
    const records = await searchStore.fetchStandardSuggestions(query)
    suggestions.value = records
    showSuggestions.value = records.length > 0
  } catch (err) {
    console.error('Error fetching suggest results', err)
    clearSuggestions()
  } finally {
    isLoadingSuggestions.value = false
  }
}

const handleInput = () => {
  const query = (searchStore['standards.documentNumber'] || '').trim()

  if (debounceTimeout) {
    window.clearTimeout(debounceTimeout)
  }

  if (!query || query.length < MIN_QUERY_LENGTH) {
    clearSuggestions()
    return
  }

  debounceTimeout = window.setTimeout(() => fetchSuggestions(query), 250)
}

const handleFocus = () => {
  if (suggestions.value.length) {
    showSuggestions.value = true
  }
}

const handleSelectSuggestion = (item: SuggestRecord) => {
  searchStore['standards.documentNumber'] = item.value;
  clearSuggestions();

  // go straight to the document details page
  navigateToDocFromSuggest(item);
};

const handleKeydown = (event: KeyboardEvent) => {
  const hasItems = suggestions.value.length > 0

  switch (event.key) {
    case 'ArrowDown':
      if (!hasItems) return
      event.preventDefault()
      showSuggestions.value = true
      highlightedIndex.value =
        highlightedIndex.value < suggestions.value.length - 1
          ? highlightedIndex.value + 1
          : 0
      break
    case 'ArrowUp':
      if (!hasItems) return
      event.preventDefault()
      showSuggestions.value = true
      highlightedIndex.value =
        highlightedIndex.value > 0
          ? highlightedIndex.value - 1
          : suggestions.value.length - 1
      break
    case 'Enter':
      if (showSuggestions.value && highlightedIndex.value >= 0 && hasItems) {
        event.preventDefault()
        handleSelectSuggestion(suggestions.value[highlightedIndex.value])
      } else {
        // normal search when no suggestion is selected
        searchStore.searchStandards(false)
        clearSuggestions()
      }
      break
    case 'Escape':
      if (showSuggestions.value) {
        event.preventDefault()
        clearSuggestions()
      }
      break
    default:
      break
  }
}

const handleShowFilterMoreOptions = () => {
  searchStore['standards.displayMoreSearchBarFilters'] =
    !searchStore['standards.displayMoreSearchBarFilters']

  if (!searchStore['standards.displayMoreSearchBarFilters']) {
    searchStore.clearSearchBarFilters(STANDARDS_SEARCH_TAB, ['documentNumber'])
  }
}

const handleClickOutside = (event: MouseEvent) => {
    showSuggestions.value = false
}

const navigateToDocFromSuggest = (item: SuggestRecord) => {
  // optional: analytics, similar to handleProductLinkClick
  // @ts-ignore
  window.dataLayer = window.dataLayer || [];
  // @ts-ignore
  window.dataLayer.push({
    event: 'SearchEvent',
    eventCategory: 'Search',
    eventAction: 'StandardSuggestDocClick',
    eventLabel: 'Standard Suggest Doc Click',
    docNo: item.value,
    docTitle: 'UNKNOWN',
    docId: item.id,
    resultCount: 'UNKNOWN'
  });

  // docOffset: we can safely use 0 like the existing example
  const docOffset = 0;

  // if you already store a search context in the store, use it;
  // otherwise this can be left as an empty string.
  const searchContext =
    (searchStore as any)['searchContext'] ||
    (searchStore as any)['standards.searchContext'] ||
    '';

  const href = `/management/display/index/${docOffset}/${item.id}/-/${searchContext}`;
  window.location.href = href;
};

const handleSearch = () => {
  searchStore.searchStandards(searchStore['searchResultActive']);
  displayAllFiltersMobile.value = false;
}

const placeholderText = computed(() => props.forMobile ? t('searchByPlaceHolderMobile') : t('searchByPlaceHolder'));

const { 
  clearAllFilters,
  clearAllSearchBarFilters
} = useSearchActions(STANDARDS_SEARCH_TAB, STANDARDS_FILTER_KEYS)

const handleClearAllFilters = () => {
    if(searchStore['searchResultActive']) {
        clearAllFilters();
    } else {
        clearAllSearchBarFilters();
    }
}
</script>

<template>
  <div class="search-input-area" v-click-outside-action="handleClickOutside">
    <!-- LEFT: search input + clear + suggestions -->
    <div class="search-input-main">
      <div class="search-input">
        <input
          type="text"
          id="query1"
          name="query1"
          :placeholder="placeholderText"
          v-model="searchStore['standards.documentNumber']"
          @input="handleInput"
          @focus="handleFocus"
          @keydown="handleKeydown"
          autocomplete="off"
        />
      </div>
      <div class="search-clear">
        <!-- IMPORTANT: use clearSearch so both text + suggestions are cleared -->
        <img
          src="@/assets/img/icon-search-clear.svg"
          @click="clearSearch"
        />
      </div>

      <!-- Autocomplete dropdown, now attached only to the left section -->
      <StandardsSuggestList
        :items="suggestions"
        :visible="showSuggestions"
        :isLoading="isLoadingSuggestions"
        :highlightedIndex="highlightedIndex"
        @select="handleSelectSuggestion"
      />
    </div>
    <!-- RIGHT: Filters, with the vertical pipeline styling unchanged -->
    <div v-if="!forMobile" class="search-filter">
      <div
        :class="[
          'btn-search-filter',
          { active: searchStore['standards.displayMoreSearchBarFilters'] }
        ]"
        @click="handleShowFilterMoreOptions"
      >
        {{ t('filters') }}
      </div>
    </div>
    <div  v-if="forMobile" class="search-filter">
      <div :class="['btn-search-filter-mobile']" @click="displayAllFiltersMobile=true"></div>
      <AllFiltersModal :display="displayAllFiltersMobile" @closeModal="displayAllFiltersMobile=false" :forMobile="true" @handleSearch="handleSearch" @clearAll="handleClearAllFilters"></AllFiltersModal>
    </div>
  </div>
</template>